/**
 * The modal shell the forgot-password dialogs share (DOP-275).
 *
 * Everything here is the part that is the same whatever the dialog is FOR:
 * the dimmed backdrop, the centered card, and the two promises `aria-modal`
 * makes — that focus starts inside, and that Tab does not walk out into the
 * page behind. Built to match the modals the app already has (see
 * `Kpi/KpiAddMetricDialog`).
 */

import React, { useEffect, useRef } from "react";

export interface ModalDialogProps {
	/** Id of the heading inside `children`, for `aria-labelledby`. */
	titleId: string;
	/**
	 * Focus target on open. Falls back to the dialog itself, which is the right
	 * answer when the first control is disabled — focusing a disabled element
	 * is a no-op that leaves focus on the page the dialog claims has gone away.
	 */
	initialFocusRef?: React.RefObject<HTMLElement | null>;
	/**
	 * Change this to pull focus back in. A dialog that swaps its contents
	 * (the two-step forgot-password flow) unmounts whatever had focus, and
	 * without this focus falls to the top of the document.
	 */
	refocusOn?: string | number;
	/**
	 * How wide the card is. The default fits a short form; `lg` and `xl` exist
	 * for dialogs that host a whole composer (DOP-478) — a form built for a page
	 * squeezed into `max-w-md` scrolls for its entire length.
	 */
	size?: "md" | "lg" | "xl";
	/** Escape lands here. Callers hand this to their own close affordance too. */
	onClose: () => void;
	children: React.ReactNode;
}

const WIDTHS: Record<NonNullable<ModalDialogProps["size"]>, string> = {
	md: "max-w-md",
	lg: "max-w-2xl",
	xl: "max-w-3xl",
};

const ModalDialog: React.FC<ModalDialogProps> = ({
	titleId,
	initialFocusRef,
	refocusOn,
	size = "md",
	onClose,
	children,
}) => {
	const dialogRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		(initialFocusRef?.current ?? dialogRef.current)?.focus();
	}, [initialFocusRef, refocusOn]);

	/**
	 * Escape closes; Tab stays inside.
	 *
	 * A dialog that says `aria-modal` and then lets Tab walk into the page
	 * behind it is lying to exactly the users who depend on the claim — and
	 * the controls back there start their own requests.
	 */
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
				return;
			}
			if (event.key !== "Tab") return;
			const dialog = dialogRef.current;
			if (!dialog) return;
			const focusable = Array.from(
				dialog.querySelectorAll<HTMLElement>(
					"button, input, select, textarea, a[href], [tabindex]:not([tabindex='-1'])",
				),
			).filter((element) => !element.hasAttribute("disabled"));
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || !dialog.contains(active))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				className={`w-full ${WIDTHS[size]} max-h-[90vh] overflow-y-auto rounded-lg border border-gray-200 bg-white p-6 shadow-lg focus:outline-none`}
			>
				{children}
			</div>
		</div>
	);
};

export default ModalDialog;
